import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigationType } from "react-router-dom";
import { Loader2, Search, SlidersHorizontal } from "lucide-react";
import { SectionTitle, GlassCard } from "../components/common/ui";
import ProductCard from "../components/common/ProductCard";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import api from "../lib/api";

const selectCls = "w-full bg-black/30 border border-white/15 text-white rounded-md px-3 py-2 text-sm";

const SORTS = [
  ["featured", "Featured"], ["name-asc", "Name A–Z"], ["price-asc", "Price low–high"],
  ["price-desc", "Price high–low"], ["newest", "Newest"], ["best-sellers", "Best sellers"],
];

export default function Catalogue() {
  const [params, setParams] = useSearchParams();
  const navType = useNavigationType();
  const restored = useRef(false);
  // Capture the saved position at mount, before the scroll listener can overwrite it during the remount.
  const targetScroll = useRef(parseInt(sessionStorage.getItem("catalogueScroll") || "0", 10));
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("search") || "");

  const get = (k, d = "") => params.get(k) || d;
  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v === "" || v == null || v === false) next.delete(k); else next.set(k, v);
    if (k !== "page") next.delete("page");
    setParams(next);
  };

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
    api.get("/products/brands").then(({ data }) => setBrands(data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const q = {
      sort: get("sort", "featured"), page: get("page", "1"), limit: 24,
      search: get("search"), category_id: get("category"), brand: get("brand"),
      stock_status: get("stock"), min_price: get("min_price") || undefined, max_price: get("max_price") || undefined,
      low_noise: get("low_noise") || undefined, family_suitable: get("family") || undefined,
    };
    Object.keys(q).forEach((k) => (q[k] === "" || q[k] == null) && delete q[k]);
    api.get("/products", { params: q }).then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const current = params.get("search") || "";
    if (search === current) return;
    const t = setTimeout(() => setParam("search", search), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Remember scroll position so returning to the catalogue (browser back) lands where you left off.
  useEffect(() => {
    const onScroll = () => sessionStorage.setItem("catalogueScroll", String(window.scrollY));
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (loading || navType !== "POP" || restored.current) return;
    restored.current = true;
    const y = targetScroll.current;
    if (!y) return;
    // Retry across frames until the grid/images have painted enough to reach the saved position.
    let tries = 0;
    const tryScroll = () => {
      window.scrollTo(0, y);
      tries += 1;
      if (Math.abs(window.scrollY - y) > 4 && tries < 40) requestAnimationFrame(tryScroll);
    };
    requestAnimationFrame(tryScroll);
  }, [loading, navType]);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16" data-testid="catalogue-page">
      <SectionTitle as="h1" eyebrow="The Range" title="All Fireworks" subtitle="Browse the full catalogue, watch demonstration videos and reserve for local collection." />

      <div className="mt-10 grid lg:grid-cols-[260px_1fr] gap-8">
        <aside className="space-y-5" data-testid="catalogue-filters">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input data-testid="catalogue-search" placeholder="Search fireworks…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-black/30 border-white/15 text-white" />
          </div>
          <GlassCard className="p-4 space-y-4" hover={false}>
            <div className="flex items-center gap-2 text-white/70 text-sm font-semibold"><SlidersHorizontal className="h-4 w-4" /> Filters</div>
            <div>
              <label className="text-xs text-white/50">Category</label>
              <select data-testid="filter-category" className={selectCls} value={get("category")} onChange={(e) => setParam("category", e.target.value)}>
                <option value="">All categories</option>
                {cats.map((c) => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-white/50">Brand</label>
              <select data-testid="filter-brand" className={selectCls} value={get("brand")} onChange={(e) => setParam("brand", e.target.value)}>
                <option value="">All brands</option>
                {brands.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-white/50">Stock status</label>
              <select data-testid="filter-stock" className={selectCls} value={get("stock")} onChange={(e) => setParam("stock", e.target.value)}>
                <option value="">All</option><option value="in_stock">In Stock</option><option value="out_of_stock">Out of Stock</option><option value="coming_soon">Coming Soon</option>
              </select>
            </div>
            <div className="flex gap-2">
              <div><label className="text-xs text-white/50">Min £</label><Input type="number" data-testid="filter-min-price" value={get("min_price")} onChange={(e) => setParam("min_price", e.target.value)} className="bg-black/30 border-white/15 text-white" /></div>
              <div><label className="text-xs text-white/50">Max £</label><Input type="number" data-testid="filter-max-price" value={get("max_price")} onChange={(e) => setParam("max_price", e.target.value)} className="bg-black/30 border-white/15 text-white" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" data-testid="filter-low-noise" checked={get("low_noise") === "true"} onChange={(e) => setParam("low_noise", e.target.checked ? "true" : "")} /> Low noise</label>
            <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" data-testid="filter-family" checked={get("family") === "true"} onChange={(e) => setParam("family", e.target.checked ? "true" : "")} /> Family friendly</label>
            <Button variant="ghost" size="sm" className="text-white/60 w-full" data-testid="clear-filters" onClick={() => { setSearch(""); setParams(new URLSearchParams()); }}>Clear all</Button>
          </GlassCard>
        </aside>

        <div>
          {cats.length > 0 && (
            <div className="mb-5 flex flex-wrap gap-2" data-testid="catalogue-category-nav">
              <button onClick={() => setParam("category", "")} className={`rounded-full px-3.5 py-1.5 text-sm border transition-colors ${!get("category") ? "border-gold bg-gold/15 text-gold" : "border-white/15 text-white/70 hover:border-white/30"}`} data-testid="catnav-all">All</button>
              {cats.map((c) => (
                <button key={c.category_id} onClick={() => setParam("category", c.category_id)} className={`rounded-full px-3.5 py-1.5 text-sm border transition-colors ${get("category") === c.category_id ? "border-gold bg-gold/15 text-gold" : "border-white/15 text-white/70 hover:border-white/30"}`} data-testid={`catnav-${c.slug}`}>{c.name}</button>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between gap-4 mb-5">
            <p className="text-sm text-muted-foreground">{data.total} product{data.total !== 1 ? "s" : ""}</p>
            <select data-testid="catalogue-sort" className="bg-black/30 border border-white/15 text-white rounded-md px-3 py-2 text-sm" value={get("sort", "featured")} onChange={(e) => setParam("sort", e.target.value)}>
              {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>
          ) : data.items.length === 0 ? (
            <GlassCard className="p-12 text-center text-muted-foreground" hover={false}>No fireworks match your filters.</GlassCard>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="catalogue-grid">
              {data.items.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
            </div>
          )}

          {data.pages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-3" data-testid="catalogue-pagination">
              <Button variant="outline" className="border-white/20 text-white" disabled={data.page <= 1} onClick={() => setParam("page", String(data.page - 1))}>Previous</Button>
              <span className="text-sm text-muted-foreground">Page {data.page} of {data.pages}</span>
              <Button variant="outline" className="border-white/20 text-white" disabled={data.page >= data.pages} onClick={() => setParam("page", String(data.page + 1))}>Next</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
